/**
 * LOS ENDPOINTS DE UN PROYECTO, AGRUPADOS POR FAMILIA (PANEL-ENDPOINTS-FAMILIAS-T1; pedido de Josse 17-sep).
 *
 * Lee lo que ya traen los nodos ep-* de nexo_catalogs.project_items (medido 15-oct: 91 nodos; consumidores en 33,
 * guardia en 32, ultimo uso en 4). Los datos vienen en formas distintas (texto, lista de textos, lista de objetos
 * {artefacto, credencial, archivo_linea}); el lector las tolera todas.
 *
 * REGLAS (las cuida scripts/check-endpoints-familias.mjs):
 *   - Familia = el primer segmento con nombre de la ruta despues de /api/(v1/), saltando "businesses"/"negocios" y los
 *     {parametros}: /api/v1/businesses/{id}/asistencia -> asistencia. Sin ruta: el "API > familia > ..." de `ruta`.
 *   - Sin la clave consumidores el nodo dice "sin dato", NO "sin consumidor": un dato que falta no es un candidato a
 *     retiro. "Sin consumidor conocido" solo con la lista vacia o un "ninguno medido" escrito.
 *   - El uso se muestra como lo escribio quien lo midio; "excesivo" solo si hay 3 o mas numeros en la familia y este
 *     pasa 10 veces la mediana. Nunca se saca un numero de un texto que no lo dice.
 *
 * Sin DOM.
 * @module application/proyectos/familiasEndpoints
 */

const txt = (v) => (v === null || v === undefined ? '' : String(v).trim());
const PREFIJOS_DE_NEGOCIO = new Set(['businesses', 'business', 'negocios', 'negocio']);

/** Las rutas del nodo, en el orden en que las escribio su autor. */
export function rutasDe(meta) {
  const e = meta && meta.endpoints;
  if (Array.isArray(e)) return e.map(txt).filter(Boolean);
  if (typeof e === 'string' && e.trim()) return [e.trim()];
  return [];
}

/** La familia de una ruta tipo "GET /api/v1/businesses/{id}/asistencia/kiosk". null si no se puede saber. */
export function familiaDeRuta(ruta) {
  const m = /\/api\/(.+)$/i.exec(txt(ruta).split(/\s+/).find((p) => p.startsWith('/')) || '');
  if (!m) return null;
  const partes = m[1].split(/[/?]/).filter(Boolean);
  for (let i = 0; i < partes.length; i++) {
    const s = partes[i].toLowerCase();
    if (/^v\d+$/.test(s) || s.startsWith('{')) continue;
    // "businesses" solo se salta cuando le sigue el {id} del negocio (/businesses/{id}/asistencia); /Businesses/all es de
    // la familia businesses.
    if (PREFIJOS_DE_NEGOCIO.has(s) && String(partes[i + 1] || '').startsWith('{') && partes[i + 2]) continue;
    return s;
  }
  return null;
}

/**
 * La familia del nodo, en este orden (medido 15-oct sobre los 91 nodos: sin los respaldos 3 y 4 quedaban 10 sueltos):
 *   1. su primera ruta en `endpoints`;
 *   2. "API > familia > ..." de `ruta`;
 *   3. el propio item_ref cuando ES una ruta (los planificados: "GET /api/v1/categorias", la captura de Josse);
 *   4. el item_ref ep-<familia>-... (ep-presupuestos -> presupuestos);
 *   si nada: "sin familia".
 */
export function familiaDe(meta, itemRef = '') {
  for (const r of rutasDe(meta)) {
    const f = familiaDeRuta(r);
    if (f) return f;
  }
  const migas = txt(meta && meta.ruta).split('>').map((s) => s.trim().toLowerCase()).filter(Boolean);
  if (migas.length >= 2 && migas[0] === 'api') return migas[1].split(/\s+/)[0];
  const ref = txt(itemRef);
  const porRuta = familiaDeRuta(ref);
  if (porRuta) return porRuta;
  const m = /^ep-([a-z0-9]+)/i.exec(ref);
  if (m) return m[1].toLowerCase();
  return 'sin familia';
}

/**
 * Consumidores: { estado: 'lista' | 'ninguno' | 'sin_dato', lista: [{artefacto, archivo, credencial}] }.
 * 'ninguno' solo cuando el nodo lo DICE (lista vacia o "ninguno medido"); sin la clave es 'sin_dato'.
 */
export function consumidoresDe(meta) {
  const c = meta ? (meta.consumidores ?? meta.consumers) : undefined;
  if (c === undefined || c === null) return { estado: 'sin_dato', lista: [] };
  const crudos = Array.isArray(c) ? c : [c];
  const lista = crudos.map((x) => (x && typeof x === 'object'
    ? { artefacto: txt(x.artefacto ?? x.nombre ?? x.quien), archivo: txt(x.archivo_linea ?? x.archivo), credencial: txt(x.credencial) }
    : { artefacto: txt(x), archivo: '', credencial: '' })).filter((x) => x.artefacto);
  if (!lista.length || lista.every((x) => /^ningun[oa]?\b/i.test(x.artefacto))) return { estado: 'ninguno', lista };
  return { estado: 'lista', lista };
}

/** El uso como lo escribio quien lo midio, y el numero de llamadas solo si el campo ES un numero. */
export function usoDe(meta) {
  const ultimo = txt(meta && meta.ultimo_uso);
  const texto = txt(meta && (meta.llamadas_7d ?? meta.llamadas_30d));
  const crudo = meta && (meta.llamadas_7d ?? null);
  const numero = typeof crudo === 'number' ? crudo : (typeof crudo === 'string' && /^\d+$/.test(crudo.trim()) ? Number(crudo) : null);
  return { ultimo, texto, llamadas7d: numero };
}

const mediana = (ns) => { const s = [...ns].sort((a, b) => a - b); const k = Math.floor(s.length / 2); return s.length % 2 ? s[k] : (s[k - 1] + s[k]) / 2; };

/**
 * Agrupa los endpoints (items con metadata ya leida) por familia, la mas grande primero.
 * @param {Array<{itemRef:string, meta:object}>} items
 * @returns {Array<{familia:string, items:Array<{itemRef, meta, consumidores, uso, excesivo:boolean}>}>}
 */
export function agruparPorFamilia(items) {
  const grupos = new Map();
  for (const it of items || []) {
    const f = familiaDe(it.meta || {}, it.itemRef);
    if (!grupos.has(f)) grupos.set(f, []);
    grupos.get(f).push({ itemRef: it.itemRef, meta: it.meta || {}, consumidores: consumidoresDe(it.meta || {}), uso: usoDe(it.meta || {}), excesivo: false });
  }
  for (const lista of grupos.values()) {
    const nums = lista.map((x) => x.uso.llamadas7d).filter((n) => n !== null);
    if (nums.length >= 3) {
      const med = mediana(nums);
      lista.forEach((x) => { x.excesivo = x.uso.llamadas7d !== null && med > 0 && x.uso.llamadas7d > med * 10; });
    }
  }
  return [...grupos.entries()].map(([familia, lista]) => ({ familia, items: lista.sort((a, b) => a.itemRef.localeCompare(b.itemRef)) }))
    .sort((a, b) => ((a.familia === 'sin familia') - (b.familia === 'sin familia')) || (b.items.length - a.items.length) || a.familia.localeCompare(b.familia));
}
